import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Paintbrush, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  RotateCcw,
  Check,
  Palette
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for edit mode
const WebshopEditorContext = createContext(null);

export const useWebshopEditor = () => useContext(WebshopEditorContext);

// Color picker popup component
const ColorPickerPopup = ({ 
  isOpen, 
  onClose, 
  position, 
  currentColor, 
  settingKey, 
  label,
  onColorChange 
}) => {
  const [color, setColor] = useState(currentColor);

  useEffect(() => {
    setColor(currentColor);
  }, [currentColor]);

  if (!isOpen) return null;

  const handleApply = () => {
    onColorChange(settingKey, color);
    onClose();
  };

  return (
    <div 
      className="fixed z-[9999] bg-[#1A1A1A] border border-white/20 rounded-xl shadow-2xl p-4 min-w-[250px]"
      style={{ 
        top: Math.min(position.y, window.innerHeight - 300),
        left: Math.min(position.x, window.innerWidth - 280)
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-white text-sm font-medium">{label}</span>
        <button onClick={onClose} className="text-white/50 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-lg border-2 border-white/20 cursor-pointer overflow-hidden"
            style={{ backgroundColor: color }}
          >
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          <Input
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="flex-1 bg-white/5 border-white/10 text-white font-mono text-sm"
            placeholder="#000000"
          />
        </div>
        
        {/* Quick colors */}
        <div className="flex flex-wrap gap-1">
          {['#FF6B00', '#F7931A', '#23F7DD', '#8B5CF6', '#EF4444', '#22C55E', '#3B82F6', '#FFFFFF', '#000000'].map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className="w-6 h-6 rounded border border-white/20 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            size="sm"
            className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
          >
            <Check className="w-4 h-4 mr-1" />
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

// Editable wrapper component
export const EditableElement = ({ 
  settingKey, 
  label, 
  type = 'background', // 'background', 'text', 'border', 'gradient-from', 'gradient-to'
  children, 
  className = '',
  style = {}
}) => {
  const editor = useWebshopEditor();
  
  if (!editor || !editor.editMode) {
    return <div className={className} style={style}>{children}</div>;
  }

  const handleClick = (e) => {
    if (!editor.editMode) return;
    e.stopPropagation();
    e.preventDefault();
    
    const rect = e.currentTarget.getBoundingClientRect();
    editor.openColorPicker({
      settingKey,
      label,
      type,
      position: { x: rect.right + 10, y: rect.top }
    });
  };

  return (
    <div 
      className={`${className} ${editor.editMode ? 'cursor-pointer ring-2 ring-transparent hover:ring-[#23F7DD] hover:ring-offset-2 hover:ring-offset-transparent transition-all' : ''}`}
      style={style}
      onClick={handleClick}
      title={editor.editMode ? `Click to edit: ${label}` : undefined}
    >
      {children}
      {editor.editMode && (
        <div className="absolute -top-2 -right-2 bg-[#23F7DD] text-[#050505] text-[10px] px-1.5 py-0.5 rounded-full font-bold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Edit
        </div>
      )}
    </div>
  );
};

// Main editor provider
export const WebshopEditorProvider = ({ children, isAdmin = false }) => {
  const [editMode, setEditMode] = useState(false);
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colorPicker, setColorPicker] = useState({
    isOpen: false,
    settingKey: '',
    label: '',
    position: { x: 0, y: 0 }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/webshop-appearance`);
      setSettings(res.data);
      setOriginalSettings(res.data);
    } catch (err) {
      console.error('Error fetching appearance:', err);
    }
  };

  const openColorPicker = ({ settingKey, label, position }) => {
    setColorPicker({
      isOpen: true,
      settingKey,
      label,
      position,
      currentColor: settings?.[settingKey] || '#FFFFFF'
    });
  };

  const closeColorPicker = () => {
    setColorPicker(prev => ({ ...prev, isOpen: false }));
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/webshop-appearance`, settings);
      setOriginalSettings(settings);
      setHasChanges(false);
      toast.success('Changes saved!');
    } catch (err) {
      console.error('Error saving:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setSettings(originalSettings);
    setHasChanges(false);
    toast.info('Changes discarded');
  };

  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
      discardChanges();
    }
    setEditMode(!editMode);
  };

  return (
    <WebshopEditorContext.Provider value={{
      editMode,
      settings,
      updateSetting,
      openColorPicker,
      toggleEditMode
    }}>
      {children}
      
      {/* Edit Mode Floating Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 right-6 z-[9998] flex items-center gap-3">
          {editMode && hasChanges && (
            <>
              <Button
                onClick={discardChanges}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 shadow-lg"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Discard
              </Button>
              <Button
                onClick={saveChanges}
                disabled={saving}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] shadow-lg"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
          <Button
            onClick={toggleEditMode}
            className={`shadow-lg ${
              editMode 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'bg-gradient-to-r from-[#FF6B00] to-[#F7931A] text-white hover:from-[#FF8C00] hover:to-[#FFCD00]'
            }`}
          >
            {editMode ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Exit Edit Mode
              </>
            ) : (
              <>
                <Paintbrush className="w-4 h-4 mr-2" />
                Edit Design
              </>
            )}
          </Button>
        </div>
      )}

      {/* Edit Mode Indicator */}
      {editMode && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9998] bg-[#23F7DD] text-[#050505] px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg">
          <Palette className="w-4 h-4" />
          EDIT MODE - Click on any element to change its color
        </div>
      )}

      {/* Color Picker Popup */}
      <ColorPickerPopup
        isOpen={colorPicker.isOpen}
        onClose={closeColorPicker}
        position={colorPicker.position}
        currentColor={colorPicker.currentColor}
        settingKey={colorPicker.settingKey}
        label={colorPicker.label}
        onColorChange={updateSetting}
      />
    </WebshopEditorContext.Provider>
  );
};

export default WebshopEditorProvider;
